export const RECORD_TYPES = [
  'idea', 'opportunity', 'project', 'active_priority',
  'task', 'decision', 'resource', 'reference',
] as const;
export type RecordType = typeof RECORD_TYPES[number];

export const STATUSES = [
  'inbox', 'now', 'next', 'later', 'parked',
  'waiting', 'blocked', 'completed', 'archived',
] as const;
export type Status = typeof STATUSES[number];

export const DIMENSIONS = [
  'vision_alignment',
  'impact',
  'revenue_potential',
  'market_window',
  'time_efficiency',
  'leverage',
  'evidence_readiness',
] as const;
export type Dimension = typeof DIMENSIONS[number];

export type Weights = Record<Dimension, number>;

export const DEFAULT_WEIGHTS: Weights = {
  vision_alignment: 0.20,
  impact: 0.15,
  revenue_potential: 0.20,
  market_window: 0.10,
  time_efficiency: 0.10,
  leverage: 0.15,
  evidence_readiness: 0.10,
};

export const TYPE_LABELS: Record<RecordType, string> = {
  idea: 'Idea',
  opportunity: 'Opportunity',
  project: 'Project',
  active_priority: 'Active Priority',
  task: 'Task',
  decision: 'Decision',
  resource: 'Resource',
  reference: 'Reference',
};

export const STATUS_LABELS: Record<Status, string> = {
  inbox: 'Inbox',
  now: 'Now',
  next: 'Next',
  later: 'Later',
  parked: 'Parked',
  waiting: 'Waiting',
  blocked: 'Blocked',
  completed: 'Completed',
  archived: 'Archived',
};

export interface GvmRecord {
  id: string;
  title: string;
  description: string;
  type: RecordType;
  parent_id: string | null;
  related_ids: string[];
  category: string;
  status: Status;
  owner: string;
  execution_lane: string;
  priority_rank: number | null;
  execution_allocation_pct: number | null;
  score_vision_alignment: number;
  score_impact: number;
  score_revenue_potential: number;
  score_market_window: number;
  score_time_efficiency: number;
  score_leverage: number;
  score_evidence_readiness: number;
  overall_score: number;
  next_action: string;
  activation_gate: string;
  current_blocker: string;
  reactivation_trigger: string;
  deadline: string | null;
  notes: string;
  raw_capture: string;
  created_at: string;
  updated_at: string;
  last_reviewed_at: string | null;
  archived: boolean;
  deleted_at: string | null;
}

export interface HistoryEntry {
  id: string; // local uuid
  record_id: string;
  timestamp: string;
  field: string | null;
  old_value: string | null;
  new_value: string | null;
  note: string;
}

export type OutboxOp =
  | { kind: 'create'; record: GvmRecord }
  | { kind: 'update'; id: string; patch: Partial<GvmRecord> & { note?: string } }
  | { kind: 'archive'; id: string }
  | { kind: 'restore'; id: string };

export interface OutboxEntry {
  seq?: number; // auto-increment key
  createdAt: string;
  op: OutboxOp;
}

export function scoresOf(r: Pick<GvmRecord,
  'score_vision_alignment' | 'score_impact' | 'score_revenue_potential' |
  'score_market_window' | 'score_time_efficiency' | 'score_leverage' | 'score_evidence_readiness'>
): Record<Dimension, number> {
  return {
    vision_alignment: r.score_vision_alignment,
    impact: r.score_impact,
    revenue_potential: r.score_revenue_potential,
    market_window: r.score_market_window,
    time_efficiency: r.score_time_efficiency,
    leverage: r.score_leverage,
    evidence_readiness: r.score_evidence_readiness,
  };
}
